"use client";

import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

import type { BlockProps, StrapiBlock } from "@/app/components/blocks/types";
import { Heading } from "@/app/design-system/atoms/Heading";

type Media = {
  id?: number;
  documentId?: string;
  url?: string | null;
  alternativeText?: string | null;
};

type Service = {
  id?: number;
  documentId?: string;
  titre?: string | null;
  icon?: Media | null;
  image?: Media | null;
  description?: string | null;
  masquer?: boolean | null;
};

type ServiceDomicileBlock = StrapiBlock & {
  __component: "blocks.service-domicile";
  titre?: string | null;
  services?: Service[];
};

export default function ServiceDomicile({
  data,
}: BlockProps<ServiceDomicileBlock>) {
  const services = Array.isArray(data.services)
  ? data.services.filter((service) => !service.masquer)
  : [];
  const [activeIndex, setActiveIndex] = useState(0);

  if (services.length === 0) return null;

  const previous = () => {
    setActiveIndex((prev) =>
      prev === 0 ? services.length - 1 : prev - 1
    );
  };

  const next = () => {
    setActiveIndex((prev) =>
      prev === services.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section className="bg-white">
      {/* TITRE PRINCIPAL */}
      {data.titre && (
        <div className="lms-container py-5 text-center lg:py-6">
          <Heading
            as="h2"
            className="font-playfair text-4xl font-bold text-textColor lg:text-[44px]"
          >
            {data.titre}
          </Heading>
        </div>
      )}

      {/* =====================================================
          MOBILE : SLIDER SANS GRANDES IMAGES
      ===================================================== */}
      <div className="pb-12 lg:hidden">
        <div className="lms-container">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${activeIndex * 100}%)`,
              }}
            >
              {services.map((service, index) => {
                const iconUrl = service.icon?.url || "";
                const iconAlt =
                  service.icon?.alternativeText ||
                  service.titre ||
                  "Service";

                return (
                  <div
                    key={service.documentId ?? service.id ?? index}
                    className="w-full shrink-0 px-2"
                  >
                    <article className="mx-auto flex min-h-[300px] max-w-[360px] flex-col items-center rounded-[18px] bg-muted px-7 py-8 text-center">
                      {/* ICÔNE */}
                      {iconUrl && (
                        <div className="flex h-[82px] w-[82px] items-center justify-center rounded-xl bg-white">
                          <div className="relative h-[60px] w-[60px]">
                            <Image
                              src={iconUrl}
                              alt={iconAlt}
                              fill
                              className="object-contain"
                              sizes="60px"
                            />
                          </div>
                        </div>
                      )}

                      {/* TITRE */}
                      {service.titre && (
                        <h3 className="mt-6 font-playfair text-2xl font-bold text-primaryGreen">
                          {service.titre}
                        </h3>
                      )}

                      {/* TRAIT */}
                      <div className="my-5 h-[3px] w-16 rounded-full bg-gradient-to-r from-primaryGreen to-greenPastel" />

                      {/* DESCRIPTION */}
                      {service.description && (
                        <p className="max-w-[300px] whitespace-pre-line text-base leading-relaxed text-textColor">
                          {service.description}
                        </p>
                      )}
                    </article>
                  </div>
                );
              })}
            </div>
          </div>

          {/* NAVIGATION MOBILE */}
          {services.length > 1 && (
            <div className="mt-7 flex items-center justify-center gap-5">
              <button
                type="button"
                onClick={previous}
                aria-label="Service précédent"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-primaryGreen text-primaryGreen"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex items-center gap-2">
                {services.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Afficher le service ${index + 1}`}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      activeIndex === index
                        ? "w-7 bg-primaryGreen"
                        : "w-2.5 bg-greenPastel"
                    }`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={next}
                aria-label="Service suivant"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-primaryGreen text-primaryGreen"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          DESKTOP : VERSION ACTUELLE AVEC IMAGES
      ===================================================== */}
      <div className="hidden lg:block">
        {services.map((service, index) => {
          const reversed = index % 2 !== 0;
          const imageUrl = service.image?.url || "";
          const imageAlt =
            service.image?.alternativeText ||
            service.titre ||
            "Service à domicile";

          const iconUrl = service.icon?.url || "";
          const iconAlt =
            service.icon?.alternativeText ||
            service.titre ||
            "Service";

          return (
            <article
              key={service.documentId ?? service.id ?? index}
              className={reversed ? "bg-white" : "bg-muted"}
            >
              <div className="lms-container">
                <div className="mx-auto grid max-w-[1200px] items-center gap-12 py-14 lg:grid-cols-2 lg:gap-20 lg:py-16">

                  {/* IMAGE */}
                  <div
                    className={`relative ${
                      reversed ? "lg:order-2" : "lg:order-1"
                    }`}
                  >
                    <div
                      className={`absolute h-[170px] w-[170px] rounded-full bg-greenPastel ${
                        reversed
                          ? "-right-8 -top-8"
                          : "-left-10 -top-10"
                      }`}
                    />

                    <div className="relative z-10 aspect-[3/2] w-full overflow-hidden rounded-[18px]">
                      {imageUrl && (
                        <Image
                          src={imageUrl}
                          alt={imageAlt}
                          fill
                          className="object-cover"
                          sizes="560px"
                        />
                      )}
                    </div>
                  </div>

                  {/* CONTENU */}
                  <div
                    className={`max-w-[470px] ${
                      reversed ? "lg:order-1" : "lg:order-2"
                    }`}
                  >
                    {/* ICÔNE */}
                    {iconUrl && (
                      <div
                        className={`mb-5 flex h-[78px] w-[78px] items-center justify-center rounded-xl ${
                          reversed ? "bg-muted" : "bg-white"
                        }`}
                      >
                        <div className="relative h-[60px] w-[60px]">
                          <Image
                            src={iconUrl}
                            alt={iconAlt}
                            fill
                            className="object-contain"
                            sizes="60px"
                          />
                        </div>
                      </div>
                    )}

                    {/* TITRE */}
                    {service.titre && (
                      <h3 className="font-playfair text-3xl font-bold text-primaryGreen">
                        {service.titre}
                      </h3>
                    )}

                    {/* TRAIT */}
                    <div className="my-5 h-[3px] w-16 rounded-full bg-gradient-to-r from-primaryGreen to-greenPastel" />

                    {/* DESCRIPTION */}
                    {service.description && (
                      <p className="whitespace-pre-line text-lg leading-relaxed text-textColor">
                        {service.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}